// ./src/components/features/EmbeddingEventTemplates/EmbeddingEventTemplateSelector/EmbeddingEventTemplateSelector.tsx
import React, { useMemo, useEffect } from "react";
import { Select } from "@mantine/core";
import { useEmbeddingEventTemplates } from "@/hooks/useEmbeddingEventTemplates";
import { LoadingIndicator } from "@/components/generic";
import { EmbeddingEventTemplate } from "@/utils/api";
import { useApp } from "@/contexts";

export interface EmbeddingEventTemplateSelectorProps {
  /** Selected template ID */
  value: string | null | undefined;

  /** Callback when template selection changes */
  onChange?: (
    templateId: string | null,
    template?: EmbeddingEventTemplate
  ) => void;

  /** Placeholder text */
  placeholder?: string;

  /** Whether to include inactive templates */
  includeInactive?: boolean;

  /** Filter by layout type */
  layoutType?: string;

  /** Custom filter function for templates */
  templateFilter?: (template: EmbeddingEventTemplate) => boolean;

  /** Whether the field is required */
  required?: boolean;

  /** Loading placeholder text */
  loadingPlaceholder?: string;

  /** Empty state text */
  emptyText?: string;

  /** Error message */
  error?: React.ReactNode;

  /** Whether the field is disabled */
  disabled?: boolean;

  /** Component size */
  size?: "xs" | "sm" | "md" | "lg" | "xl";

  /** Additional props passed to Select */
  [key: string]: any;
}

export const EmbeddingEventTemplateSelector: React.FC<
  EmbeddingEventTemplateSelectorProps
> = ({
  value,
  onChange,
  placeholder = "Select a template",
  includeInactive = false,
  layoutType,
  templateFilter,
  required = false,
  loadingPlaceholder = "Loading templates...",
  emptyText = "No templates available",
  error,
  disabled,
  size = "md",
  ...selectProps
}) => {
  // Use templates hook
  const { isAuthenticated } = useApp();
  const { embeddingEventTemplates, loading, isEmpty, lastError } =
    useEmbeddingEventTemplates({
      autoLoad: true,
      isAuthenticated,
    });

  // Filter and prepare template options
  const templateOptions = useMemo(() => {
    let filteredTemplates = embeddingEventTemplates;

    // Apply status filter (status 100 = active, 0 = disabled)
    if (!includeInactive) {
      filteredTemplates = filteredTemplates.filter(
        (template) => template.status === 100 // Only show active templates
      );
    }

    // Apply layout type filter if provided
    if (layoutType) {
      filteredTemplates = filteredTemplates.filter(
        (template) => template.layoutType === layoutType
      );
    }

    // Apply custom filter if provided
    if (templateFilter) {
      filteredTemplates = filteredTemplates.filter(templateFilter);
    }

    // Sort by name for better UX
    filteredTemplates.sort((a, b) => {
      const nameA = a.name || "Unnamed Template";
      const nameB = b.name || "Unnamed Template";
      return nameA.localeCompare(nameB);
    });

    // Convert to select options
    return filteredTemplates.map((template) => ({
      value: template.id || "",
      label: template.name || "Unnamed Template",
      disabled: template.status === 0, // Disabled templates have status 0
    }));
  }, [embeddingEventTemplates, includeInactive, layoutType, templateFilter]);

  // Create a map for quick template lookup by ID
  const templateMap = useMemo(() => {
    const map = new Map();
    embeddingEventTemplates.forEach((template) => {
      if (template.id) {
        map.set(template.id, template);
      }
    });
    return map;
  }, [embeddingEventTemplates]);

  // Auto-select first template if required and no value provided
  useEffect(() => {
    if (
      required &&
      templateOptions.length > 0 &&
      !loading &&
      onChange &&
      (value === null || value === undefined)
    ) {
      const firstTemplate = templateOptions[0];
      if (firstTemplate && !firstTemplate.disabled) {
        const templateObject = templateMap.get(firstTemplate.value);
        onChange(firstTemplate.value, templateObject);
      }
    }
  }, [required, value, templateOptions, loading, onChange, templateMap]);

  // Handle onChange with template object lookup
  const handleChange = (selectedValue: string | null) => {
    if (selectedValue) {
      const selectedTemplate = templateMap.get(selectedValue);
      onChange?.(selectedValue, selectedTemplate);
    } else {
      // When clearing selection, pass null for both ID and template
      onChange?.(null, undefined);
    }
  };

  // Determine the current state and configure select props accordingly
  const getSelectState = () => {
    // Loading state
    if (loading && embeddingEventTemplates.length === 0) {
      return {
        data: [],
        placeholder: loadingPlaceholder,
        disabled: true,
        rightSection: <LoadingIndicator size={size} color="gray" />,
      };
    }

    // Error state
    if (lastError && !loading) {
      return {
        data: [],
        placeholder: "Error loading templates",
        disabled: true,
        error: error || "Failed to load templates",
      };
    }

    // Empty state
    if (isEmpty && !loading) {
      return {
        data: [],
        placeholder: emptyText,
        disabled: true,
      };
    }

    // Normal state with data
    return {
      data: templateOptions,
      placeholder,
      disabled: disabled || loading,
      rightSection:
        loading && embeddingEventTemplates.length > 0 ? (
          <LoadingIndicator size={size} color="gray" />
        ) : (
          selectProps.rightSection
        ),
    };
  };

  const selectState = getSelectState();

  // Normalize value for the select component
  const normalizedValue = value ?? null;

  // Render single select
  return (
    <Select
      {...selectProps}
      size={size}
      data={selectState.data}
      value={normalizedValue}
      onChange={handleChange}
      placeholder={selectState.placeholder}
      clearable={!required}
      searchable
      required={required}
      disabled={selectState.disabled}
      error={selectState.error || error}
      rightSection={selectState.rightSection}
    />
  );
};
